import { useApolloClient } from '@apollo/client';
import momentjs from 'moment';
import * as Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import _ from 'underscore';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Modal,
	Paragraph,
	Popover,
	Progress as ProgressBar,
	Spinner,
	tokens,
} from '@treams/treams-ui';

const Progress = styled(Box)`
	color: ${tokens.text.secondary};
`;

export default function InboxManagerCsvExport(props: {
	ids: string[];
	dateRange: [Date, Date];
	onFinish: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { loading: rolesLoading, isAdministrator, isWorkspaceSuperuser } = useViewerRoles();

	// The API only returns the username to workspace admins and super users.
	const canViewUsername = isAdministrator || isWorkspaceSuperuser;

	const [progress, setProgress] = useState(0.0);
	const [url, setUrl] = useState<string | undefined>(undefined);

	const client = useApolloClient();

	const momentTypes: Record<graphql.MomentType, string> = {
		[graphql.MomentType.Evaluation]: 'Evaluation Moment',
		[graphql.MomentType.Feedback]: 'Feedback Moment',
		[graphql.MomentType.Goals]: 'Goal Setting Moment',
		[graphql.MomentType.Message]: 'Message Moment',
		[graphql.MomentType.TalentReview]: 'Talent Review Moment',
	};

	const conversationStatuses: Record<graphql.EvaluationSubmissionStatus, string> = {
		[graphql.EvaluationSubmissionStatus.Draft]: 'Draft',
		[graphql.EvaluationSubmissionStatus.Shared]: 'Shared',
		[graphql.EvaluationSubmissionStatus.Signed]: 'Signed',
	};

	useEffect(() => {
		// Starting before the roles resolve would omit the username column for
		// viewers that are allowed to see it.
		if (rolesLoading) {
			return;
		}

		async function download() {
			const chunks = _.chunk(props.ids, 10);

			const results: graphql.User[] = [];

			for (const chunk of chunks) {
				const response = await client.query<graphql.ManagerInboxCsvExportQuery>({
					query: graphql.ManagerInboxCsvExportDocument,
					variables: {
						ids: chunk,
						startDate: props.dateRange[0],
						endDate: props.dateRange[1],
					},
				});

				results.push(...(response.data.usersByIDs as graphql.User[]));

				setProgress(results.length / props.ids.length);
			}

			function formatPercentage(value: number): string {
				if (value.valueOf() !== value) {
					return '-';
				}

				return (value * 100).toFixed(0) + '%';
			}

			function isMomentCompleted(moment: graphql.MomentInstance) {
				return moment.status === graphql.MomentStatus.Completed;
			}

			const records = results
				.flatMap((user) => user.moments.map((moment) => ({ user, moment })))
				.map(({ user, moment }) => [
					...(canViewUsername ? [user.username] : []),
					user.firstName,
					user.lastName,
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(localize(user.currentJobPosition?.jobTitle.title ?? ([] as any)) as any)?.text ?? '',
					user.group.name,
					momentTypes[moment.momentTemplate.type],
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(localize(moment.momentTemplate.title) as any).text,
					localize(
						moment.evaluation?.evaluationForm?.title ??
							moment.momentTemplate.evaluation?.evaluationForm?.title,
					)?.text ?? '-',
					momentjs(new Date(moment.startDate)).format('DD-MM-YYYY'),
					momentjs(new Date(moment.endDate)).format('DD-MM-YYYY'),
					isMomentCompleted(moment as graphql.MomentInstance) ? 'Yes' : 'No',
					new Date(moment.endDate) < new Date() ? 'Yes' : 'No',
					moment.evaluation?.manager
						? [
								moment.evaluation.manager.user.firstName,
								moment.evaluation.manager.user.lastName,
							].join(' ')
						: '-',
					moment.evaluation ? conversationStatuses[moment.evaluation.manager.status] : '-',
					moment.evaluation ? conversationStatuses[moment.evaluation.employee.status] : '-',
					moment.goals
						? formatPercentage(
								moment.goals
									.map((goal) =>
										goal.status === graphql.GoalStatus.Completed ? 1.0 : goal.progress,
									)
									.reduce((p, c) => p + c, 0.0) / moment.goals.length,
							)
						: '-',
					moment.feedbackRequest
						? formatPercentage(
								moment.feedbackRequest.responses.edges.filter(
									({ node }) => node.status === graphql.FeedbackResponseStatus.Shared,
								).length / moment.feedbackRequest.responses.edges.length,
							)
						: '-',
				]);

			const data = Papa.unparse([
				[
					...(canViewUsername ? ['Username'] : []),
					'First Name',
					'Last Name',
					'Job',
					'Department',
					'Moment Type',
					'Moment Name',
					'Conversation Template',
					'Moment Start Date',
					'Moment End Date',
					'Moment Completed',
					'Moment Due',
					'Conversation Partner',
					'Conversation Status Manager',
					'Conversation Status Employee',
					'Goal Progress',
					'Feedback Progress',
				],
				...records,
			]);
			const blob = new Blob([data], { type: 'text/csv' });
			setUrl(URL.createObjectURL(blob));
		}

		download();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rolesLoading]);

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header
					title={t('inbox.manager.csvExportModal.title')}
					onClose={() => props.onFinish()}
				/>

				<Modal.Contents>
					{url ? (
						<Flex alignItems="center" flexDirection="column">
							<Paragraph>{t('inbox.manager.csvExportModal.success.message')}</Paragraph>
							<Box mt={3}>
								<Button.Anchor
									type="primary"
									href={url}
									download={t('inbox.manager.csvExportModal.fileName', {
										date: momentjs().format('DD-MM-YYYY'),
									})}
								>
									{t('inbox.manager.csvExportModal.downloadButton.title')}
								</Button.Anchor>
							</Box>
						</Flex>
					) : (
						<Flex alignItems="center" flexDirection="column">
							<Spinner mb={3} />

							<Paragraph>{t('inbox.manager.csvExportModal.explanation')}</Paragraph>
							<Flex alignItems="center" mt={3}>
								<Box>
									<ProgressBar value={progress} />
								</Box>
								<Progress ml={2}>{(progress * 100).toFixed(0)}%</Progress>
							</Flex>
						</Flex>
					)}
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}
